import {
    useCallback,
    useEffect,
    Fragment,
    useState,
    type FC
} from "react";
import {
    Pressable,
    Animated,
    View
} from "react-native";
import type IAvatarProps from "./type";
import type {
    AvatarStateLayerPlacement,
    AvatarImageErrorEvent,
    AvatarImageLoadEvent
} from "./type";
import stylesheet, {
    AVATAR_STATUS_LABELS,
    getAvatarStatusColor,
    getAvatarImageCached,
    getAvatarBadgeColors,
    getAvatarRingColor,
    getAvatarImageKey,
    getAvatarInitials,
    getAvatarSizeKey,
    getAvatarColors,
    getAvatarSize,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    useAnimatedState,
    usePressScale,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    User as UserIcon
} from "lucide-react-native";
import {
    FIXED_BOX_MAX_FONT_SCALE
} from "../../helpers/theme/typography";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";
import Loading from "../loading";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const Avatar: FC<IAvatarProps> = ({
    avatarBackgroundColor: avatarBackgroundColorProp,
    titleAbbreviationType = "first-last",
    statusIndicatorType = "online",
    isShowStatusIndicatorSplicer,
    isShowStatusIndicatorBorder,
    isStatusIndicator = false,
    isWorkWithAction = false,
    "aria-label": ariaLabel,
    isShowBorder = false,
    accessibilityLabel,
    isDisabled = false,
    isLoading = false,
    image: ImageProp,
    type = "neutral",
    backgroundColor,
    size = "medium",
    customLocalize,
    icon: IconProp,
    borderColor,
    customTheme,
    imageSource,
    onLongPress,
    accessible,
    imageProps,
    onHoverOut,
    onPressOut,
    titleColor,
    iconColor,
    onHoverIn,
    onPressIn,
    imageUrl,
    onFocus,
    onPress,
    onBlur,
    badge,
    style,
    title,
    role,
    ...props
}) => {
    const {
        surfaceColor,
        touchTargets,
        avatarSizes,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const avatarBackgroundColor = avatarBackgroundColorProp ?? surfaceColor;

    const [
        erroredImageKey,
        setErroredImageKey
    ] = useState<string>();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const isActionable = isWorkWithAction;

    const pressScaleStyle = usePressScale({
        isDisabled: isDisabled || isLoading,
        isEnabled: isActionable,
        customTheme,
        isPressed
    });

    const currentSize = getAvatarSize({
        avatarSizes,
        iconSizes,
        borders,
        size
    });

    const initials = getAvatarInitials({
        titleAbbreviationType,
        locale: activeLocale,
        title
    });

    const imageKey = getAvatarImageKey({
        image: ImageProp,
        imageSource,
        imageUrl
    });

    const [
        loadedImageKey,
        setLoadedImageKey
    ] = useState<string | undefined>(() => getAvatarImageCached({
        imageKey
    }) ? imageKey : undefined);

    const onImageErrorProp = imageProps?.onError;

    const onImageLoadProp = imageProps?.onLoad;

    const isImageVisible = !isLoading && !!imageKey && erroredImageKey !== imageKey;

    const isImageLoaded = !!ImageProp || loadedImageKey === imageKey;

    const imageOpacity = useAnimatedState({
        value: isImageLoaded ? 1 : 0,
        intent: "surfaceEnter",
        motion: "fade",
        customTheme
    });

    const {
        isRendered: isFallbackRendered,
        progress: fallbackProgress
    } = usePresence({
        isVisible: isImageVisible && !isImageLoaded,
        enterIntent: "surfaceEnter",
        exitIntent: "surfaceExit",
        customTheme
    });

    const isPlaceholder = isLoading || (!isImageVisible && !initials);

    const stateLayerPlacement: AvatarStateLayerPlacement = isImageVisible ? "end" : "start";

    const currentColors = getAvatarColors({
        surfaceColor: avatarBackgroundColor,
        backgroundColor,
        isPlaceholder,
        titleColor,
        isDisabled,
        iconColor,
        colors,
        type
    });

    const isCurrentShowStatusIndicatorBorder = isShowStatusIndicatorBorder ?? isShowStatusIndicatorSplicer ?? true;

    const badgeColor = getAvatarBadgeColors({
        isDisabled,
        colors,
        badge
    });

    const {
        statusIndicator: statusIndicatorDynamicStyle,
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        image: imageDynamicStyle,
        badge: badgeDynamicStyle
    } = useStyles({
        isShowStatusIndicatorBorder: isCurrentShowStatusIndicatorBorder,
        floorColor: colors.content.container[avatarBackgroundColor],
        ringColor: isShowBorder ? getAvatarRingColor({
            avatarBackgroundColor,
            borderColor,
            colors
        }) : undefined,
        statusColor: getAvatarStatusColor({
            statusIndicatorType,
            colors
        }),
        isFocusVisible,
        currentColors,
        isActionable,
        touchTargets,
        currentSize,
        pointerType,
        badgeColor,
        isLoading,
        radiuses,
        borders,
        colors
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const isShowStatusIndicator = isStatusIndicator && !badge;

    const badgeLabel = badge?.accessibilityLabel?.trim();

    const statusLabel = isShowStatusIndicator ? localize(AVATAR_STATUS_LABELS[statusIndicatorType]) : badgeLabel;

    const getAccessibilityLabel = () => {
        const label = ariaLabel || accessibilityLabel || title?.trim();

        if(label && statusLabel) {
            return localize("avatar-with-status", [
                label,
                statusLabel
            ]);
        }

        if(label || statusLabel) {
            return label || statusLabel;
        }

        if(isLoading) {
            return localize("loading");
        }

        return undefined;
    };

    const currentLabel = getAccessibilityLabel();

    useEffect(() => {
        const sizeKey = getAvatarSizeKey({
            size
        });

        if(sizeKey === size) {
            return;
        }

        warnOnce({
            message: `Avatar: size "${size}" is deprecated and will be removed in the next major version. Use "${sizeKey}" instead, the largest step of the avatar ladder.`
        });
    }, [
        size
    ]);

    useEffect(() => {
        if(!badge || !isStatusIndicator) {
            return;
        }

        warnOnce({
            message: "Avatar: \"badge\" and \"isStatusIndicator\" use the same slot, so the badge is drawn and the status indicator is not."
        });
    }, [
        isStatusIndicator,
        badge
    ]);

    useEffect(() => {
        if(isShowStatusIndicatorSplicer === undefined) {
            return;
        }

        warnOnce({
            message: "Avatar: \"isShowStatusIndicatorSplicer\" is deprecated and will be removed in the next major version. Use \"isShowStatusIndicatorBorder\" instead."
        });
    }, [
        isShowStatusIndicatorSplicer
    ]);

    useEffect(() => {
        if(!isActionable || ariaLabel || accessibilityLabel || title?.trim()) {
            return;
        }

        warnOnce({
            message: "Avatar: an actionable avatar without a \"title\" needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        isActionable,
        ariaLabel,
        title
    ]);

    const onImageError = useCallback((event: AvatarImageErrorEvent) => {
        setErroredImageKey(imageKey);

        onImageErrorProp?.(event);
    }, [
        onImageErrorProp,
        imageKey
    ]);

    const onImageLoad = useCallback((event: AvatarImageLoadEvent) => {
        setLoadedImageKey(imageKey);

        onImageLoadProp?.(event);
    }, [
        onImageLoadProp,
        imageKey
    ]);

    const renderStateLayer = (placement: AvatarStateLayerPlacement) => {
        if(placement !== stateLayerPlacement) {
            return null;
        }

        return <StateLayer
            isHovered={!isLoading && isHovered}
            isPressed={!isLoading && isPressed}
            borderRadius={radiuses.full}
            customTheme={customTheme}
        />;
    };

    const renderBadge = (BadgeIcon: NCoreUIKitIcon) => {
        return <View
            style={[
                stylesheet.badge,
                badgeDynamicStyle
            ]}
            aria-hidden={true}
        >
            <BadgeIcon
                customColor={badgeColor.iconColor}
                size={currentSize.badgeIconSize}
                color="contrastHigh"
            />
        </View>;
    };

    const renderStatusIndicator = () => {
        if(badge) {
            return renderBadge(badge.icon);
        }

        if(!isStatusIndicator) {
            return null;
        }

        return <View
            style={[
                stylesheet.statusIndicator,
                statusIndicatorDynamicStyle
            ]}
            aria-hidden={true}
        />;
    };

    const renderLoading = () => {
        return <Loading
            customColor={currentColors.iconColor}
            size={currentSize.loadingSize}
            customTheme={customTheme}
        />;
    };

    const renderIcon = () => {
        if(IconProp) {
            return <IconProp
                customColor={currentColors.iconColor}
                color={currentColors.iconColorKey}
                size={currentSize.iconSize}
            />;
        }

        return <UserIcon
            color={currentColors.iconColor}
            size={currentSize.iconSize}
        />;
    };

    const renderImage = () => {
        if(ImageProp) {
            return <ImageProp
                {...imageProps}
                height={currentSize.size}
                width={currentSize.size}
                style={[
                    imageProps?.style,
                    stylesheet.image,
                    imageDynamicStyle
                ]}
                onError={onImageError}
            />;
        }

        return <Animated.Image
            fadeDuration={0}
            {...imageProps}
            style={[
                imageProps?.style,
                stylesheet.image,
                imageDynamicStyle,
                {
                    opacity: imageOpacity
                }
            ]}
            height={currentSize.size}
            width={currentSize.size}
            onError={onImageError}
            onLoad={onImageLoad}
            source={{
                uri: imageUrl,
                ...imageSource
            }}
        />;
    };

    const renderInitials = () => {
        return <Text
            maxFontSizeMultiplier={FIXED_BOX_MAX_FONT_SCALE}
            customColor={currentColors.titleColor}
            variant={currentSize.fontSize}
            customTheme={customTheme}
            style={stylesheet.title}
            numberOfLines={1}
        >
            {initials}
        </Text>;
    };

    const renderFallback = () => {
        if(initials) {
            return renderInitials();
        }

        return renderIcon();
    };

    const renderImageWithFallback = () => {
        if(ImageProp) {
            return renderImage();
        }

        return <Fragment>
            {
                isFallbackRendered ?
                    <Animated.View
                        style={[
                            stylesheet.fallback,
                            {
                                opacity: fallbackProgress
                            }
                        ]}
                    >
                        {renderFallback()}
                    </Animated.View>
                :
                    null
            }
            {renderImage()}
        </Fragment>;
    };

    const renderContent = () => {
        if(isLoading) {
            return renderLoading();
        }

        if(isImageVisible) {
            return renderImageWithFallback();
        }

        if(initials) {
            return renderInitials();
        }

        return renderIcon();
    };

    if(!isActionable) {
        return <View
            {...props}
            role={role ?? (currentLabel ? "img" : undefined)}
            accessible={accessible ?? !!currentLabel}
            aria-label={currentLabel}
            style={[
                style,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
            aria-busy={isLoading}
        >
            {renderContent()}
            {renderStatusIndicator()}
        </View>;
    }

    return <AnimatedPressable
        {...props}
        {...interactionHandlers}
        onLongPress={isLoading ? undefined : onLongPress}
        onPress={isLoading ? undefined : onPress}
        aria-disabled={isDisabled}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle,
            pressScaleStyle
        ]}
        aria-label={currentLabel}
        accessible={accessible}
        role={role ?? "button"}
        aria-busy={isLoading}
        disabled={isDisabled}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            {renderStateLayer("start")}
            {renderContent()}
            {renderStateLayer("end")}
            {renderStatusIndicator()}
        </View>
    </AnimatedPressable>;
};
export default Avatar;
